import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export const STUDIO_STEPS = [
  { id: 1, label: 'Idea & Format' },
  { id: 2, label: 'Render Style' },
  { id: 3, label: 'Screenplay' },
  { id: 4, label: 'Cast & Voices' },
  { id: 5, label: 'Storyboard & Shots' },
  { id: 6, label: 'Sound' },
  { id: 7, label: 'Final Cut' },
];

const finalHint = (fc) => ({ running: `Cutting · ${fc.progress || 0}%`, completed: `v${fc.version || 1} ready`, failed: 'Needs retry' }[fc.status] || 'Free · 0 cr');

/** Derives real per-step progress from the project document — nothing is hard-locked. */
export function stepStatus(project, styleLabel) {
  const scenes = project.scenes || [];
  const anchored = new Set(scenes.flatMap((s) => s.anchor_asset_ids || []));
  const kit = (project.show_kit || []).filter((a) => a.kind !== 'product' && anchored.has(a.id));
  const locked = kit.filter((a) => a.reference_urls?.length).length;
  const rendered = scenes.filter((s) => s.video_url).length;
  const native = project.audio_mode === 'native';
  const lines = scenes.filter((s) => s.dialogue || s.narration);
  const voiced = lines.filter((s) => s.audio_url || (native && s.audio_fallback !== 'cast')).length;
  const fc = project.final_cut || {};
  return {
    1: { done: true, hint: `${project.format === 'series' ? `Series · ${project.episodes_count || 1} eps` : 'Film'} · ${project.aspect_ratio}` },
    2: { done: !!project.render_style, hint: styleLabel || 'Pick a look' },
    3: { done: scenes.length > 0, hint: scenes.length ? `${scenes.length} shots written` : 'Not written yet' },
    4: { done: scenes.length > 0 && locked >= kit.length, hint: kit.length ? `${locked}/${kit.length} cast locked` : 'No cast yet' },
    5: { done: scenes.length > 0 && rendered >= scenes.length, hint: `${rendered}/${scenes.length} rendered` },
    6: { done: scenes.length > 0 && voiced >= lines.length, hint: native ? 'Native AI audio' : lines.length ? `${voiced}/${lines.length} voiced` : 'No spoken lines' },
    7: { done: fc.status === 'completed', hint: finalHint(fc) },
  };
}

export const firstOpenStep = (status) => STUDIO_STEPS.find((s) => !status[s.id].done)?.id || 7;

export function StudioStepper({ status, current, onSelect }) {
  return (
    <nav className="sticky top-[4.5rem] z-20 overflow-x-auto rounded-xl border border-neutral-800 bg-neutral-950/85 p-1.5 backdrop-blur-xl" data-testid="studio-stepper">
      <ol className="flex min-w-max items-center">
        {STUDIO_STEPS.map((s, i) => {
          const st = status[s.id];
          const active = current === s.id;
          return (
            <li key={s.id} className="flex items-center">
              <button type="button" onClick={() => onSelect(s.id)} data-testid={`studio-step-${s.id}`} aria-current={active ? 'step' : undefined}
                className={cn('flex items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors duration-200',
                  active ? 'bg-amber-500/15 ring-1 ring-amber-400/50' : 'hover:bg-neutral-900')}>
                <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full border font-mono text-xs transition-colors duration-200',
                  active ? 'border-amber-400 bg-amber-500 text-neutral-900' : st.done ? 'border-emerald-400/70 bg-emerald-500/15 text-emerald-200' : 'border-neutral-700 text-neutral-400')}>
                  {st.done && !active ? <Check size={13} /> : s.id}
                </span>
                <span className="leading-tight">
                  <span className={cn('block text-xs font-medium', active ? 'text-amber-100' : 'text-neutral-200')}>{s.label}</span>
                  <span className="block max-w-[150px] truncate text-[10px] text-neutral-500" data-testid={`studio-step-${s.id}-hint`}>{st.hint}</span>
                </span>
              </button>
              {i < STUDIO_STEPS.length - 1 && <span className={cn('mx-0.5 h-px w-4', st.done ? 'bg-emerald-500/50' : 'bg-neutral-800')} />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function StepNav({ current, onSelect }) {
  const next = STUDIO_STEPS.find((s) => s.id === current + 1);
  return (
    <div className="flex items-center justify-between border-t border-neutral-900 pt-5">
      <Button variant="ghost" disabled={current === 1} onClick={() => onSelect(current - 1)} className="text-neutral-400 hover:text-neutral-100" data-testid="studio-step-back">
        <ArrowLeft size={14} className="mr-1" /> Back
      </Button>
      {next && (
        <Button onClick={() => onSelect(next.id)} className="bg-neutral-100 text-neutral-900 hover:bg-white" data-testid="studio-step-next">
          Continue · {next.id} {next.label} <ArrowRight size={14} className="ml-1" />
        </Button>
      )}
    </div>
  );
}
